import {
	type Cell,
	flexRender,
	type Row,
	type RowSelectionState,
	type Table,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
	ChevronRight,
	ChevronsDownUp,
	ChevronsUpDown,
	Plus,
} from "lucide-react";
import {
	type CSSProperties,
	memo,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { AutoCombineEntryV2, EntryMiniTransaction } from "@/shared/cache";
import { SpellcheckedNarrative } from "@/shared/components/SpellcheckedNarrative";
import { DataTableResizeHandle } from "@/shared/components/ui/data-table-resize-handle";
import {
	Collapsible,
	CollapsibleContent,
} from "@/v2/components/ui/collapsible";
import { Skeleton } from "@/v2/components/ui/skeleton";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { cn } from "@/v2/lib/utils";
import { AutoCombineMiniTransactions } from "./AutoCombineMiniTransactions";
import { TIMER_COLUMN_ID } from "./autoCombineColumns";
import { NARRATIVE_MIN_PX } from "./columns";
import { NarrativeRegenerationStatus } from "./NarrativeRegenerationStatus";
import { SelectionProvider, useSelectionAPI } from "./selection";

const EXPAND_GUTTER_PX = 40;
const ACTIONS_GUTTER_PX = 8;
const SKELETON_ROW_COUNT = 15;
const PARENT_ROW_ESTIMATE = 54;
const MINI_ROW_ESTIMATE = 38;
const EMPTY_STREAMING_NARRATIVES: Record<string, string> = {};

export interface MoveMode {
	active: boolean;
	isValidTarget: (entry: AutoCombineEntryV2) => boolean;
	onMove: (entry: AutoCombineEntryV2) => void;
}

export interface NewGroupAction {
	disabledReason?: string | null;
	onClick: () => void;
}

interface AutoCombineTableProps {
	table: Table<AutoCombineEntryV2>;
	rowSelection: RowSelectionState;
	isLoading: boolean;
	expandedIds: Set<string>;
	onToggleExpand: (id: string) => void;
	onExpandAll: () => void;
	onCollapseAll: () => void;
	selectedMiniIds: Set<string>;
	selectedGroupIds: Set<string>;
	onToggleMini: (id: string) => void;
	onEditMini: (entry: AutoCombineEntryV2, mini: EntryMiniTransaction) => void;
	onRowClick: (entry: AutoCombineEntryV2) => void;
	moveMode: MoveMode;
	newGroupAction?: NewGroupAction | null;
	pendingIds?: Set<string>;
	regeneratingNarrativeIds?: Set<string>;
	streamingNarratives?: Record<string, string>;
	emptyMessage?: string;
	readOnly?: boolean;
	hasMore?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
}

export function AutoCombineTable({
	table,
	rowSelection,
	isLoading,
	expandedIds,
	onToggleExpand,
	onExpandAll,
	onCollapseAll,
	selectedMiniIds,
	selectedGroupIds,
	onToggleMini,
	onEditMini,
	onRowClick,
	moveMode,
	newGroupAction,
	pendingIds = new Set(),
	regeneratingNarrativeIds = new Set(),
	streamingNarratives = EMPTY_STREAMING_NARRATIVES,
	emptyMessage = "No entry groups yet.",
	readOnly = false,
	hasMore = false,
	isLoadingMore = false,
	onLoadMore,
}: AutoCombineTableProps) {
	const rows = table.getRowModel().rows;
	const hasData = table.getCoreRowModel().rows.length > 0;
	const allExpanded =
		rows.length > 0 && rows.every((r) => expandedIds.has(r.original.id));

	const columnSizing = table.getState().columnSizing;
	const columnVisibility = table.getState().columnVisibility;
	const columnOrder = table.getState().columnOrder;

	// biome-ignore lint/correctness/useExhaustiveDependencies: deps are state subscriptions; `table` is stable.
	const { gridTemplate, totalWidth } = useMemo(() => {
		const tracks: string[] = [`${EXPAND_GUTTER_PX}px`];
		let width = EXPAND_GUTTER_PX;
		for (const col of table.getVisibleLeafColumns()) {
			if (col.id === "description") {
				tracks.push(`minmax(${NARRATIVE_MIN_PX}px, 1fr)`);
				width += NARRATIVE_MIN_PX;
			} else {
				const size = col.getSize();
				tracks.push(`${size}px`);
				width += size;
			}
		}
		tracks.push(`${ACTIONS_GUTTER_PX}px`);
		width += ACTIONS_GUTTER_PX;
		return { gridTemplate: tracks.join(" "), totalWidth: width };
	}, [table, columnSizing, columnVisibility, columnOrder]);

	const rootStyle = useMemo(
		() =>
			({
				"--auto-cols": gridTemplate,
				"--auto-width": `${totalWidth}px`,
			}) as CSSProperties,
		[gridTemplate, totalWidth],
	);
	const rowStyle = useMemo(
		() => ({ gridTemplateColumns: "var(--auto-cols)" }) as CSSProperties,
		[],
	);
	const scrollerRef = useRef<HTMLDivElement>(null);
	const headerScrollerRef = useRef<HTMLDivElement>(null);
	const [scrollbarWidth, setScrollbarWidth] = useState(0);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const updateScrollbarWidth = () => {
			setScrollbarWidth(scroller.offsetWidth - scroller.clientWidth);
		};

		updateScrollbarWidth();
		const resizeObserver = new ResizeObserver(updateScrollbarWidth);
		resizeObserver.observe(scroller);
		window.addEventListener("resize", updateScrollbarWidth);
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateScrollbarWidth);
		};
	}, []);

	const headerFullWidthStyle: CSSProperties = {
		gridTemplateColumns: "var(--auto-cols)",
		width: scrollbarWidth > 0 ? `calc(100% - ${scrollbarWidth}px)` : "100%",
		minWidth: "var(--auto-width)",
	};

	const lastScrollLeftRef = useRef(0);
	const handleBodyScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const { scrollLeft, scrollHeight, scrollTop, clientHeight } =
				event.currentTarget;
			if (scrollLeft !== lastScrollLeftRef.current) {
				lastScrollLeftRef.current = scrollLeft;
				if (headerScrollerRef.current) {
					headerScrollerRef.current.scrollLeft = scrollLeft;
				}
			}
			if (
				hasMore &&
				!isLoadingMore &&
				scrollHeight - scrollTop - clientHeight < 400
			) {
				onLoadMore?.();
			}
		},
		[hasMore, isLoadingMore, onLoadMore],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: column state subscriptions; `table` is stable.
	const visibleColumnIds = useMemo(
		() => table.getVisibleLeafColumns().map((col) => col.id),
		[table, columnVisibility, columnOrder],
	);
	const visibleColumnKey = visibleColumnIds.join("|");

	return (
		<SelectionProvider
			orderedRows={rows}
			selection={rowSelection}
			setSelection={table.setRowSelection}
		>
			<div
				style={rootStyle}
				className="relative flex h-full w-full flex-col bg-background"
			>
				<div
					ref={headerScrollerRef}
					className="shrink-0 overflow-hidden border-b border-border bg-muted"
				>
					<div className="grid" style={headerFullWidthStyle}>
						<div className="flex h-10 items-center justify-center">
							{rows.length > 0 && (
								<button
									type="button"
									title={allExpanded ? "Collapse all" : "Expand all"}
									aria-label={allExpanded ? "Collapse all" : "Expand all"}
									onClick={allExpanded ? onCollapseAll : onExpandAll}
									className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
								>
									{allExpanded ? (
										<ChevronsDownUp className="size-4" />
									) : (
										<ChevronsUpDown className="size-4" />
									)}
								</button>
							)}
						</div>
						{table.getHeaderGroups().map((hg) =>
							hg.headers.map((h) => (
								<div
									key={h.id}
									className="relative flex h-10 min-w-0 items-center overflow-hidden px-3 text-xs"
								>
									{h.isPlaceholder
										? null
										: flexRender(h.column.columnDef.header, h.getContext())}
									<DataTableResizeHandle header={h} />
								</div>
							)),
						)}
						<div aria-hidden />
					</div>
				</div>

				<div
					ref={scrollerRef}
					className="min-h-0 flex-1 overflow-auto"
					onScroll={handleBodyScroll}
				>
					{isLoading && !hasData ? (
						<AutoCombineSkeleton
							rowStyle={rowStyle}
							visibleColumnIds={visibleColumnIds}
						/>
					) : rows.length === 0 ? (
						<div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
							{emptyMessage}
						</div>
					) : (
						<VirtualizedAutoCombineBody
							rows={rows}
							scrollerRef={scrollerRef}
							rowStyle={rowStyle}
							visibleColumnIds={visibleColumnIds}
							visibleColumnKey={visibleColumnKey}
							expandedIds={expandedIds}
							onToggleExpand={onToggleExpand}
							selectedMiniIds={selectedMiniIds}
							selectedGroupIds={selectedGroupIds}
							onToggleMini={onToggleMini}
							onEditMini={onEditMini}
							onRowClick={onRowClick}
							moveMode={moveMode}
							pendingIds={pendingIds}
							regeneratingNarrativeIds={regeneratingNarrativeIds}
							streamingNarratives={streamingNarratives}
							readOnly={readOnly}
						/>
					)}
				</div>
				{newGroupAction ? (
					<MoveSelectionToNewGroupRow action={newGroupAction} />
				) : null}
			</div>
		</SelectionProvider>
	);
}

function MoveSelectionToNewGroupRow({ action }: { action: NewGroupAction }) {
	const disabled = Boolean(action.disabledReason);
	return (
		<TooltipButton
			type="button"
			variant="ghost"
			size="sm"
			disabled={disabled}
			onClick={action.onClick}
			tooltip={action.disabledReason ?? "Move selection to new group"}
			className="h-10 w-full rounded-none border-t border-border bg-background/95 text-sm font-medium text-muted-foreground shadow-none backdrop-blur-sm hover:bg-muted/60 hover:text-foreground disabled:opacity-60"
			wrapperClassName="flex w-full shrink-0"
		>
			<Plus className="size-4" />
			Move selection to new group
		</TooltipButton>
	);
}

function VirtualizedAutoCombineBody({
	rows,
	scrollerRef,
	rowStyle,
	visibleColumnIds,
	visibleColumnKey,
	expandedIds,
	onToggleExpand,
	selectedMiniIds,
	selectedGroupIds,
	onToggleMini,
	onEditMini,
	onRowClick,
	moveMode,
	pendingIds,
	regeneratingNarrativeIds,
	streamingNarratives,
	readOnly,
}: {
	rows: Row<AutoCombineEntryV2>[];
	scrollerRef: React.RefObject<HTMLDivElement | null>;
	rowStyle: CSSProperties;
	visibleColumnIds: string[];
	visibleColumnKey: string;
	expandedIds: Set<string>;
	onToggleExpand: (id: string) => void;
	selectedMiniIds: Set<string>;
	selectedGroupIds: Set<string>;
	onToggleMini: (id: string) => void;
	onEditMini: (entry: AutoCombineEntryV2, mini: EntryMiniTransaction) => void;
	onRowClick: (entry: AutoCombineEntryV2) => void;
	moveMode: MoveMode;
	pendingIds: Set<string>;
	regeneratingNarrativeIds: Set<string>;
	streamingNarratives: Record<string, string>;
	readOnly: boolean;
}) {
	const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
		count: rows.length,
		getScrollElement: () => scrollerRef.current,
		estimateSize: (index) => {
			const entry = rows[index]?.original;
			if (!entry || !expandedIds.has(entry.id)) return PARENT_ROW_ESTIMATE;
			return (
				PARENT_ROW_ESTIMATE +
				Math.max(1, entry.miniTransactions.length) * MINI_ROW_ESTIMATE
			);
		},
		getItemKey: (index) => rows[index]?.id ?? index,
		measureElement: (element) => element?.getBoundingClientRect().height ?? 0,
		overscan: 8,
	});

	const virtualItems = rowVirtualizer.getVirtualItems();

	return (
		<div
			className="relative w-full min-w-[var(--auto-width)]"
			style={{ height: rowVirtualizer.getTotalSize() }}
		>
			{virtualItems.map((virtualRow) => {
				const row = rows[virtualRow.index];
				if (!row) return null;
				return (
					<div
						key={virtualRow.key}
						data-index={virtualRow.index}
						ref={rowVirtualizer.measureElement}
						className="absolute left-0 top-0 w-full"
						style={{ transform: `translateY(${virtualRow.start}px)` }}
					>
						<AutoCombineRow
							entry={row.original}
							row={row}
							rowStyle={rowStyle}
							visibleColumnIds={visibleColumnIds}
							visibleColumnKey={visibleColumnKey}
							expanded={expandedIds.has(row.original.id)}
							onToggleExpand={onToggleExpand}
							selectedMiniIds={selectedMiniIds}
							selectedGroupIds={selectedGroupIds}
							onToggleMini={onToggleMini}
							onEditMini={onEditMini}
							onRowClick={onRowClick}
							moveMode={moveMode}
							pendingIds={pendingIds}
							regeneratingNarrativeIds={regeneratingNarrativeIds}
							streamingNarrative={streamingNarratives[row.original.id]}
							readOnly={readOnly}
						/>
					</div>
				);
			})}
		</div>
	);
}

interface AutoCombineRowProps {
	entry: AutoCombineEntryV2;
	row: Row<AutoCombineEntryV2>;
	rowStyle: CSSProperties;
	visibleColumnIds: string[];
	visibleColumnKey: string;
	expanded: boolean;
	onToggleExpand: (id: string) => void;
	selectedMiniIds: Set<string>;
	selectedGroupIds: Set<string>;
	onToggleMini: (id: string) => void;
	onEditMini: (entry: AutoCombineEntryV2, mini: EntryMiniTransaction) => void;
	onRowClick: (entry: AutoCombineEntryV2) => void;
	moveMode: MoveMode;
	pendingIds: Set<string>;
	regeneratingNarrativeIds: Set<string>;
	streamingNarrative?: string;
	readOnly: boolean;
}

const AutoCombineRow = memo(function AutoCombineRow({
	entry,
	row,
	rowStyle,
	visibleColumnIds,
	expanded,
	onToggleExpand,
	selectedMiniIds,
	selectedGroupIds,
	onToggleMini,
	onEditMini,
	onRowClick,
	moveMode,
	pendingIds,
	regeneratingNarrativeIds,
	streamingNarrative,
	readOnly,
}: AutoCombineRowProps) {
	const { handleSelect } = useSelectionAPI();
	const isExported = entry.status === "exported";

	const cellsByColumnId = useMemo(
		() =>
			new Map<string, Cell<AutoCombineEntryV2, unknown>>(
				row.getAllCells().map((cell) => [cell.column.id, cell]),
			),
		[row],
	);

	const isTargetable = moveMode.active && moveMode.isValidTarget(entry);
	const isInvalidTarget = moveMode.active && !isTargetable;
	const isPending = pendingIds.has(entry.id);
	const isRegeneratingNarrative = regeneratingNarrativeIds.has(entry.id);
	const isStreamingNarrative = streamingNarrative !== undefined;

	const handleRowClick = (e: React.MouseEvent) => {
		if (readOnly) return;
		if (isPending) return;
		if (e.ctrlKey || e.metaKey || e.shiftKey) {
			handleSelect(row.id, e);
			return;
		}
		if (moveMode.active) {
			if (isTargetable) moveMode.onMove(entry);
			return;
		}
		if (isExported) return; // exported entry groups are inert except for the checkbox
		onRowClick(entry);
	};

	return (
		<Collapsible open={expanded} className="border-b border-border">
			{/* biome-ignore lint/a11y/useSemanticElements: CSS grid rows can't use table markup. */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled via the checkbox + chevron controls. */}
			<div
				role="row"
				tabIndex={readOnly || moveMode.active ? -1 : 0}
				data-state={row.getIsSelected() ? "selected" : undefined}
				className={cn(
					"group/row relative grid transition-colors",
					readOnly ? "cursor-default" : "cursor-pointer",
					// Parents stay on the base surface while their source panel carries
					// the subtle tint — the white→tint rhythm chunks a
					// fully-expanded ledger into entries. Hover tint is scoped to content
					// cells so the chevron and checkbox gutters can hover independently;
					// selection still tints the full row. Move-target blue tints the
					// full row too, but its stronger hover state stays scoped to content
					// cells so utility gutters keep their own interaction surfaces.
					!moveMode.active &&
						"bg-background data-[state=selected]:bg-muted/50 [&:has([data-row-content]:hover)>[data-row-content]]:bg-muted/40 [&:has([data-row-content]:hover)[data-state=selected]>[data-row-content]]:bg-muted/60",
					isTargetable &&
						"cursor-pointer bg-[color-mix(in_oklch,var(--brand-blue)_14%,transparent)] [&:has([data-row-content]:hover)>[data-row-content]]:bg-[color-mix(in_oklch,var(--brand-blue)_20%,transparent)]",
					isInvalidTarget && "cursor-default opacity-40",
					isPending && "cursor-default opacity-60",
				)}
				style={rowStyle}
				onMouseDown={(e) => {
					if (e.shiftKey) e.preventDefault();
				}}
				onClick={handleRowClick}
			>
				<div className="flex items-center justify-center">
					<button
						type="button"
						aria-label={
							expanded ? "Collapse source entries" : "Expand source entries"
						}
						aria-expanded={expanded}
						onClick={(e) => {
							e.stopPropagation();
							onToggleExpand(entry.id);
						}}
						className={cn(
							"flex h-full w-full cursor-pointer items-center justify-center transition-colors hover:text-foreground",
							isTargetable
								? "hover:bg-[color-mix(in_oklch,var(--brand-blue)_20%,transparent)]"
								: "hover:bg-muted/50",
							expanded ? "text-foreground" : "text-muted-foreground",
						)}
					>
						<ChevronRight
							className={cn(
								"size-4 transition-transform duration-150 ease-out",
								expanded && "rotate-90",
							)}
						/>
					</button>
				</div>
				{visibleColumnIds.map((columnId) => {
					const cell = cellsByColumnId.get(columnId);
					if (!cell) return null;
					return (
						<div
							key={cell.id}
							data-row-content={
								columnId === "_select" || columnId === TIMER_COLUMN_ID
									? undefined
									: ""
							}
							className={cn(
								"flex min-w-0 flex-col justify-center text-sm transition-colors",
								columnId === "_select"
									? "p-0 [&>button]:m-0 [&>button]:h-full [&>button]:w-full [&>button]:rounded-none"
									: columnId === TIMER_COLUMN_ID
										? "items-center px-1 py-2.5"
										: "px-3 py-2.5",
							)}
						>
							{columnId === "description" && isStreamingNarrative ? (
								<SpellcheckedNarrative
									text={streamingNarrative}
									debounceMs={300}
									className="whitespace-pre-wrap text-foreground leading-snug"
								/>
							) : columnId === "description" && isRegeneratingNarrative ? (
								<NarrativeRegenerationStatus />
							) : (
								flexRender(cell.column.columnDef.cell, cell.getContext())
							)}
						</div>
					);
				})}
				<div aria-hidden />
			</div>

			<CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
				<AutoCombineMiniTransactions
					entry={entry}
					visibleColumnIds={visibleColumnIds}
					selectedMiniIds={selectedMiniIds}
					groupSelected={selectedGroupIds.has(entry.id)}
					onToggleMini={onToggleMini}
					onEditMini={onEditMini}
					pendingIds={pendingIds}
					readOnly={readOnly}
				/>
			</CollapsibleContent>
		</Collapsible>
	);
}, areAutoCombineRowsEqual);

function areAutoCombineRowsEqual(
	prev: AutoCombineRowProps,
	next: AutoCombineRowProps,
) {
	return (
		prev.entry === next.entry &&
		prev.row === next.row &&
		prev.rowStyle === next.rowStyle &&
		prev.visibleColumnKey === next.visibleColumnKey &&
		prev.expanded === next.expanded &&
		prev.selectedMiniIds === next.selectedMiniIds &&
		prev.selectedGroupIds === next.selectedGroupIds &&
		prev.onToggleExpand === next.onToggleExpand &&
		prev.onToggleMini === next.onToggleMini &&
		prev.onEditMini === next.onEditMini &&
		prev.onRowClick === next.onRowClick &&
		prev.moveMode === next.moveMode &&
		prev.pendingIds === next.pendingIds &&
		prev.regeneratingNarrativeIds === next.regeneratingNarrativeIds &&
		prev.streamingNarrative === next.streamingNarrative &&
		prev.readOnly === next.readOnly
	);
}

function AutoCombineSkeleton({
	rowStyle,
	visibleColumnIds,
}: {
	rowStyle: CSSProperties;
	visibleColumnIds: string[];
}) {
	return (
		<div className="min-w-[var(--auto-width)]" aria-busy="true">
			<output className="sr-only">Loading entry groups…</output>
			{Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: fixed-count placeholders.
					key={i}
					className="grid min-h-[48px] border-b border-border"
					style={rowStyle}
				>
					<div className="flex items-center justify-center">
						<Skeleton className="size-3.5 rounded-sm" />
					</div>
					{visibleColumnIds.map((columnId) => (
						<div
							key={columnId}
							className="flex min-w-0 flex-col justify-center px-3 py-2.5"
						>
							<AutoCombineSkeletonCell columnId={columnId} rowIndex={i} />
						</div>
					))}
					<div aria-hidden />
				</div>
			))}
		</div>
	);
}

function AutoCombineSkeletonCell({
	columnId,
	rowIndex,
}: {
	columnId: string;
	rowIndex: number;
}) {
	const narrativeWidth = ["w-11/12", "w-4/5", "w-full"][rowIndex % 3];
	const matterTopWidth = ["w-24", "w-10", "w-28"][rowIndex % 3];
	const matterMiddleWidth = ["w-32", "w-4", "w-36"][rowIndex % 3];
	const matterBottomWidth = ["w-28", "w-0", "w-24"][rowIndex % 3];
	const codeBottomWidth = ["w-0", "w-11", "w-0"][rowIndex % 3];

	switch (columnId) {
		case "_select":
			return <Skeleton className="mx-auto size-4 rounded-sm" />;
		case "timekeeper":
			return <Skeleton className="h-2.5 w-20" />;
		case "date":
			return <Skeleton className="h-3 w-10" />;
		case "matter":
			return (
				<div className="space-y-1">
					<Skeleton className={cn("h-2.5", matterTopWidth)} />
					<Skeleton className={cn("h-2.5", matterMiddleWidth)} />
					{matterBottomWidth !== "w-0" ? (
						<Skeleton className={cn("h-2.5", matterBottomWidth)} />
					) : null}
				</div>
			);
		case "codes":
			return (
				<div className="space-y-1">
					<Skeleton className="h-2.5 w-9" />
					{codeBottomWidth !== "w-0" ? (
						<Skeleton className={cn("h-2.5", codeBottomWidth)} />
					) : null}
				</div>
			);
		case "description":
			return (
				<div className="space-y-1.5">
					<Skeleton className={cn("h-3", narrativeWidth)} />
					{rowIndex % 4 === 0 ? <Skeleton className="h-3 w-2/3" /> : null}
				</div>
			);
		case "status":
		case "billableCategory":
			return <Skeleton className="mx-auto size-4 rounded-full" />;
		case "units":
			return <Skeleton className="mx-auto h-5 w-10 rounded-[6px]" />;
		case TIMER_COLUMN_ID:
			return null;
		case "application":
			return <Skeleton className="mx-auto size-6 rounded-md" />;
		default:
			return <Skeleton className="h-3 w-3/4" />;
	}
}
